import React from 'react';
import {
  PurchaseOrderItem,
  GlobalCostCenter,
  CostCenterStrategy,
  MockProject,
  MockWorkCategory
} from './PurchaseOrderFormData';
import { FileText, Building2, Briefcase } from 'lucide-react';

interface OrderSummaryProps {
  items: PurchaseOrderItem[];
  strategy: CostCenterStrategy;
  globalCostCenter: GlobalCostCenter | null;
  currency: string;
  projects: MockProject[];
  generalWorkCategories: MockWorkCategory[];
  accountingType: 'A' | 'B';
  ivaCode: string | null;
}

interface ItemSummary {
  itemId: string;
  description: string;
  unit: string;
  unitPrice: number;
  totalQuantity: number;
  totalAmount: number;
  distributions: {
    costCenterType: 'PROJECT' | 'GENERAL';
    costCenterName: string;
    workCategoryName: string;
    quantity: number;
    amount: number;
  }[];
}

export const OrderSummary: React.FC<OrderSummaryProps> = ({
  items,
  strategy,
  globalCostCenter,
  currency,
  projects,
  generalWorkCategories,
  accountingType,
  ivaCode
}) => {
  if (items.length === 0) {
    return null;
  }

  const itemSummaries: ItemSummary[] = items.map(item => {
    const totalQuantity = strategy === 'GLOBAL' || strategy === 'UNASSIGNED'
      ? (item.quantity || 0)
      : item.allocations.reduce((sum, alloc) => sum + alloc.allocatedQuantity, 0);

    const totalAmount = totalQuantity * item.unitPrice;

    let distributions;

    if (strategy === 'UNASSIGNED') {
      const systemDefaultCategory = generalWorkCategories.find(c => c.isSystemDefault);
      distributions = [{
        costCenterType: 'GENERAL' as const,
        costCenterName: 'Rubro General',
        workCategoryName: systemDefaultCategory?.name || 'Sin asignar',
        quantity: totalQuantity,
        amount: totalAmount,
      }];
    } else if (strategy === 'GLOBAL' && globalCostCenter) {
      distributions = [{
        costCenterType: globalCostCenter.costCenterType,
        costCenterName: globalCostCenter.costCenterType === 'PROJECT'
          ? projects.find(p => p.id === globalCostCenter.projectId)?.name || 'Proyecto desconocido'
          : 'Rubro General',
        workCategoryName: globalCostCenter.costCenterType === 'PROJECT'
          ? projects.find(p => p.id === globalCostCenter.projectId)?.workCategories.find(c => c.id === globalCostCenter.workCategoryId)?.name || 'Rubro desconocido'
          : generalWorkCategories.find(c => c.id === globalCostCenter.workCategoryId)?.name || 'Rubro desconocido',
        quantity: totalQuantity,
        amount: totalAmount,
      }];
    } else {
      distributions = item.allocations.map(alloc => ({
        costCenterType: alloc.costCenterType,
        costCenterName: alloc.costCenterType === 'PROJECT'
          ? projects.find(p => p.id === alloc.projectId)?.name || 'Proyecto desconocido'
          : 'Rubro General',
        workCategoryName: alloc.costCenterType === 'PROJECT'
          ? projects.find(p => p.id === alloc.projectId)?.workCategories.find(c => c.id === alloc.workCategoryId)?.name || 'Rubro desconocido'
          : generalWorkCategories.find(c => c.id === alloc.workCategoryId)?.name || 'Rubro desconocido',
        quantity: alloc.allocatedQuantity,
        amount: alloc.allocatedQuantity * item.unitPrice,
      }));
    }

    return {
      itemId: item.id,
      description: item.description,
      unit: item.unit,
      unitPrice: item.unitPrice,
      totalQuantity,
      totalAmount,
      distributions,
    };
  });

  const grandTotal = itemSummaries.reduce((sum, item) => sum + item.totalAmount, 0);

  const costCenterTotals = new Map<string, { name: string; categoryName: string; total: number }>();

  itemSummaries.forEach(item => {
    item.distributions.forEach(dist => {
      const key = `${dist.costCenterName}|${dist.workCategoryName}`;
      if (costCenterTotals.has(key)) {
        costCenterTotals.get(key)!.total += dist.amount;
      } else {
        costCenterTotals.set(key, {
          name: dist.costCenterName,
          categoryName: dist.workCategoryName,
          total: dist.amount,
        });
      }
    });
  });

  return (
    <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">
      <div className="px-4 py-5 border-b sm:px-6 border-border">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
          <FileText className="w-5 h-5 text-primary" />
          Resumen de la Orden
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Detalle completo de items y distribución de costos
        </p>
      </div>

      <div className="px-4 py-6 sm:px-6 space-y-4">
        {/* Items Detail */}
        <div className="space-y-4">
          {itemSummaries.map((item, index) => (
            <div key={item.itemId} className="p-4 border rounded-lg border-border bg-muted/30">
              {/* Item Header */}
              <div className="flex items-start justify-between pb-3 mb-3 border-b border-border">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full bg-primary text-primary-foreground">
                      {index + 1}
                    </span>
                    <h3 className="text-base font-semibold text-foreground">{item.description}</h3>
                  </div>
                  <div className="flex gap-4 ml-8 text-sm text-muted-foreground">
                    <span><strong>Precio Unit.:</strong> {currency} {item.unitPrice.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                    <span><strong>Cantidad:</strong> {item.totalQuantity.toFixed(2)} {item.unit}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">Total Item</div>
                  <div className="text-xl font-bold text-primary">
                    {currency} {item.totalAmount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              {/* Distributions */}
              {item.distributions.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-muted-foreground uppercase">Distribución:</div>
                  <div className="space-y-2">
                    {item.distributions.map((dist, distIndex) => (
                      <div key={distIndex} className="flex items-center justify-between p-2 bg-card border rounded-lg border-border">
                        <div className="flex items-center gap-2">
                          {dist.costCenterType === 'PROJECT' ? (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary">
                              <Building2 className="w-3 h-3 mr-1" />
                              {dist.costCenterName}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-success/10 text-success">
                              <Briefcase className="w-3 h-3 mr-1" />
                              Rubro General
                            </span>
                          )}
                          <span className="text-sm text-muted-foreground">→ {dist.workCategoryName}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-muted-foreground">{dist.quantity.toFixed(2)} {item.unit}</span>
                          <span className="text-sm font-semibold text-foreground">
                            {currency} {dist.amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Cost Center Totals */}
        {costCenterTotals.size > 1 && (
          <div className="p-4 border-2 rounded-lg border-primary/20 bg-primary/5">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Totales por Centro de Costo:</h3>
            <div className="space-y-2">
              {Array.from(costCenterTotals.entries()).map(([key, value]) => (
                <div key={key} className="flex justify-between p-2 bg-card rounded-lg border border-border">
                  <span className="text-sm font-medium text-foreground">
                    {value.name} → {value.categoryName}
                  </span>
                  <span className="text-sm font-bold text-primary">
                    {currency} {value.total.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Grand Total */}
        <div className="p-5 space-y-3 border-2 rounded-lg border-primary/20 bg-primary/5">
          <div className="pb-2 border-b border-primary/20">
            <div className="text-sm font-semibold text-foreground">TOTAL ORDEN DE COMPRA</div>
            <div className="text-xs text-muted-foreground">{itemSummaries.length} item{itemSummaries.length !== 1 ? 's' : ''}</div>
          </div>

          <div className="space-y-2">
            {/* Subtotal */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">Subtotal:</span>
              <span className="text-lg font-semibold text-foreground">
                {currency} {grandTotal.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>

            {/* IVA */}
            {accountingType === 'A' && ivaCode && (() => {
              const ivaAmount = grandTotal * (parseFloat(ivaCode) / 100);
              const totalWithIva = grandTotal + ivaAmount;

              return (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">IVA ({ivaCode}%):</span>
                    <span className="text-lg font-semibold text-foreground">
                      {currency} {ivaAmount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>

                  <div className="pt-2 mt-2 border-t-2 border-primary/30">
                    <div className="flex items-center justify-between">
                      <span className="text-base font-bold text-foreground">TOTAL:</span>
                      <span className="text-3xl font-bold text-primary">
                        {currency} {totalWithIva.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </>
              );
            })()}

            {/* Total sin IVA (Tipo B) */}
            {accountingType === 'B' && (
              <div className="pt-2 mt-2 border-t-2 border-primary/30">
                <div className="flex items-center justify-between">
                  <span className="text-base font-bold text-foreground">TOTAL:</span>
                  <span className="text-3xl font-bold text-primary">
                    {currency} {grandTotal.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">(IVA no discriminado)</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
